import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLearner } from '../../hooks/useLearner';
import { useProgress } from '../../hooks/useProgress';
import { useChildStories } from '../../hooks/useChildStories';
import { getBuddyPortraitPath } from '../../lib/buddyAssets';
import LevelRing from '../../components/map/LevelRing';
import dailyStoryAvailableIcon from '../../assets/icons/daily-story-available.webp';
import dailyStoryDoneIcon from '../../assets/icons/daily-story-done.webp';
import storyShipCardIcon from '../../assets/icons/story-ship-card.webp';
import s from './LearnerSidebar.module.css';

const DESKTOP_BREAKPOINT = 1280;
const SPRING = { type: 'spring', stiffness: 380, damping: 36 };

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT
  );
  useEffect(() => {
    const mql = window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`);
    const onChange = (e) => setIsDesktop(e.matches);
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else mql.removeListener(onChange);
    };
  }, []);
  return isDesktop;
}

/**
 * Desktop-only collapsible learner panel anchored to the left edge.
 * Hidden entirely below 1280px viewport width.
 */
export default function LearnerSidebar({ onOpenStoryShip }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isDesktop = useIsDesktop();
  const { data: activeLearner } = useLearner();
  const { data: progress } = useProgress(activeLearner?.id);
  const { data: storiesData } = useChildStories(activeLearner?.id);
  const dailyLimitUsed = !!storiesData?.daily_limit_used;

  const [collapsed, setCollapsed] = useState(false);

  // FP-CLIENT-SUBMIT-GUARD-01: immediate, single-fire feedback on Create. Ref
  // blocks a same-frame second tap; state disables the card (reusing the
  // existing actionCardDisabled style). Unmounts on navigate → latch resets.
  const navigatingRef = useRef(false);
  const [navigating, setNavigating] = useState(false);

  function handleDailyStory() {
    if (dailyLimitUsed || navigatingRef.current) return;
    navigatingRef.current = true;
    setNavigating(true);
    navigate('/story/topic-picker');
  }

  if (!isDesktop) return null;
  if (!activeLearner) return null;

  const buddyImg = getBuddyPortraitPath(activeLearner?.buddy?.slug);
  const currentLevel = progress?.level?.current ?? 0;
  const nextLevel = currentLevel + 1;
  const xpProgressPct = progress?.level?.xp_progress_pct ?? 0;
  const xpAtNextLevel = progress?.level?.xp_at_next_level ?? 0;
  const xpTotal = progress?.level?.xp_total ?? 0;
  const xpToNext = Math.max(0, xpAtNextLevel - xpTotal);
  const streak = progress?.streak?.current ?? 0;
  const knownCount = progress?.vocab?.known_count ?? 0;
  const curriculumSize = progress?.vocab?.curriculum_size ?? null;

  return (
    <AnimatePresence initial={false} mode="wait">
        {collapsed ? (
          <motion.button
            key="collapsed"
            type="button"
            className={s.collapsedTab}
            onClick={() => setCollapsed(false)}
            initial={{ x: -120, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -120, opacity: 0 }}
            transition={SPRING}
            aria-label="Expand learner panel"
          >
            {buddyImg ? (
              <img src={buddyImg} alt={activeLearner.name} className={s.collapsedAvatar} />
            ) : (
              <span className={s.collapsedFallback}>{activeLearner.name?.[0] ?? '·'}</span>
            )}
            <span className={s.collapsedChevron} aria-hidden="true">›</span>
          </motion.button>
        ) : (
          <motion.aside
            key="expanded"
            className={s.sidebar}
            initial={{ x: -420, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -420, opacity: 0 }}
            transition={SPRING}
            aria-label="Learner panel"
          >
            <div className={s.avatarFloat}>
              {buddyImg && (
                <img src={buddyImg} alt={activeLearner.name} className={s.avatarImg} />
              )}
            </div>

            <button
              type="button"
              className={s.collapseBtn}
              onClick={() => setCollapsed(true)}
              aria-label="Collapse panel"
            >
              ‹
            </button>

            <div className={s.identity}>
              <p className={s.name}>{activeLearner.name}</p>
              <p className={s.subtitle}>{activeLearner.curriculum?.name || activeLearner.curriculum?.slug || 'Framework'}</p>
            </div>

            <div className={s.statsCard}>
              <LevelRing level={currentLevel} progressPct={xpProgressPct} />
              <div className={s.statRows}>
                <p className={s.statRow}>
                  <span className={s.statAccent}>
                    {curriculumSize ? `${knownCount}/${curriculumSize}` : `${knownCount}`}
                  </span>{' '}
                  {t('map.charactersKnown')}
                </p>
                <p className={s.statRowSub}>
                  {t('map.xpToLevel', { xp: xpToNext.toLocaleString(), level: nextLevel })}
                </p>
              </div>

              <div className={s.streakPill}>
                <span aria-hidden="true">🔥</span>
                <span>{t('map.dayStreak', { count: streak })}</span>
              </div>
            </div>

            <button
              type="button"
              className={`${s.actionCard} ${dailyLimitUsed || navigating ? s.actionCardDisabled : ''}`}
              onClick={handleDailyStory}
              disabled={dailyLimitUsed || navigating}
              aria-disabled={dailyLimitUsed || navigating}
            >
              <div className={s.actionIcon} aria-hidden="true">
                {dailyLimitUsed ? (
                  <img
                    src={dailyStoryDoneIcon}
                    alt=""
                    width={148}
                    height={116}
                    className={s.actionIconImg}
                  />
                ) : (
                  <img
                    src={dailyStoryAvailableIcon}
                    alt=""
                    width={94}
                    height={106}
                    className={s.actionIconImg}
                  />
                )}
              </div>
              <div className={s.actionCopy}>
                {!dailyLimitUsed && (
                  <p className={s.actionEyebrow}>{t('dashboard.dailyStoryTime')}</p>
                )}
                <p className={s.actionTitle}>
                  {dailyLimitUsed ? t('dashboard.seeYouTomorrow') : t('dashboard.comeUpWithStory')}
                </p>
              </div>
            </button>

            <button
              type="button"
              className={s.shipCard}
              onClick={onOpenStoryShip}
            >
              <div className={s.shipIcon} aria-hidden="true">
                <img
                  src={storyShipCardIcon}
                  alt=""
                  style={{ width: '100%', height: 'auto', objectFit: 'contain' }}
                />
              </div>
              <div className={s.actionCopy}>
                <p className={s.actionEyebrow}>{t('map.storyShipEyebrow')}</p>
                <p className={s.actionTitle}>{t('map.storyShipTitle')}</p>
              </div>
            </button>
          </motion.aside>
        )}
      </AnimatePresence>
  );
}
